import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import { HttpClient, HttpParams } from '@angular/common/http';

import { AuthenticationService } from '../authentication/authentication.service';
import { api } from '../api';
import { Storage } from '../constants';
import { Inject } from '@angular/core';
import { DOCUMENT } from '@angular/platform-browser';

/**
 * Translation Loader Service.
 */
@Injectable()
export class TranslationLoaderService {
  /** @member {string} currentCulture Culture of currect user. */
  private currentCulture: string;

  /** @member {string[]} availableTranslationCategories Translations for
   * categories which are already provided to ngx-translate. */
  private availableTranslationCategories: string[] = [];

  constructor(private translate: TranslateService, private authService: AuthenticationService,
    private http: HttpClient, @Inject(DOCUMENT) private _document: any
  ) {
    // Set culture on app initialization
    this.currentCulture = this.getCurrentCulture();
    this._document.documentElement.lang = this.currentCulture;
    translate.use(this.currentCulture);
  }

  /**
   * This method provides new translations to ngx-translate service.
   * This can also be used from modules/components to get additional translations for app.
   * Calling this method merges new translations with existing ones(if not already available).
   * @param {string[]} keys Translation definition keys.
   * @returns {Observable<boolean>} Should scribe to this to know when translations
   *                            are provided to ngx-translate. This will be useful
   *                            when need to halt redering untill translations are available.
   */
  public getTranslations(keys: string[]): Observable<boolean> {
    // First remove translation categories from keys for which translations are
    // already available
    const filteredKeys = keys.filter(key => this.availableTranslationCategories.indexOf(key) < 0);

    if (filteredKeys.length === 0) {
      // No translations categories to fetch.
      return Observable.of(true);
    }

    // First check the cache
    const notInCacheKeys: string[] = [];
    for (const key of filteredKeys) {
      const cachedTranslation = this.getFromLocalStorage(key);
      if (cachedTranslation == null) {
        notInCacheKeys.push(key);
      } else {
        this.setTranslation(cachedTranslation);
      }
    }

    // Fetch translations from server which are not available in cache.
    if (notInCacheKeys.length > 0) {
      return this.getTranslationFromServer(notInCacheKeys.join(','))
        .map((translationsObject) => {
          for (const translation of translationsObject) {
            this.setTranslation(translation);
            this.addToLocalStorage(translation);
          }
        })
        .switchMap(() => Observable.of(true));
    } else {
      return Observable.of(true);
    }
  }

  getUserSelctedCulture() {
    return this.currentCulture;
  }
  /**
   * Call this method when user logs in to see if current culture has changed,
   * If culture is changed, fetch new translations.
   */
  public checkCultureChange(selectedCulture?: string) {
    let culture = '';
    if (selectedCulture) {
      culture = selectedCulture;
    } else {
      culture = this.getCurrentCulture();
    }

    if (this.currentCulture !== culture) {
      this.currentCulture = culture;
      this._document.documentElement.lang = this.currentCulture;
      const availableTranslations = this.availableTranslationCategories;
      this.availableTranslationCategories = [];
      this.getTranslations(availableTranslations)
        .subscribe((success) => {
          this.translate.use(this.currentCulture);
        });
    }
  }

  /**
   * Get current user's culture
   */
  public getCurrentCulture() {
    let culture = 'en-US';
    if (this.authService.isUserLoggedin()) {
      culture = this.parseToken(this.getAuthToken())['cul'];
    } else {
      culture = this.translate.getBrowserCultureLang() || 'en-US';
    }
    return culture;
  }

  /**
   * Append translations to already available translations for ngx-translate
   * @param {Object} translation Translations JSON
   * @returns {void}
   */
  private setTranslation(translation: Object): void {
    // Append translations
    this.translate.setTranslation(this.currentCulture, translation['table'], true);
    // Push translation category to available translations array
    this.availableTranslationCategories.push(translation['moduleName']);
  }

  /**
   * Get translation object for a category from local storage.
   * @param {string} key Translation definition category.
   * @returns {JSON|null} JSON Translation object or null if expired or doesn't exist.
   */
  private getFromLocalStorage(key: string): Object | null {
    // Get from local storage.
    const localStorageKey = this.getLocalStorageKey(key);
    const cachedTranslation = JSON.parse(window.localStorage.getItem(localStorageKey));
    if (cachedTranslation == null) {
      return null;
    }

    // Check is translation cache is still valid.
    if (this.isTranslationCacheValid(cachedTranslation['moduleName'], cachedTranslation['hash'])) {
      return cachedTranslation;
    } else {
      window.localStorage.removeItem(localStorageKey);
      return null;
    }
  }

  /**
   * Fetch translations from server. This is used when translations are not available
   * in local storage.
   * @param {string} keys Comma separated list of translation categories
   * @returns {Observable<any>} Translations JSON Object
   */
  private getTranslationFromServer(keys: string): Observable<any> {
    const httpParams: HttpParams = new HttpParams().set('keys', keys);
    return this.http.get(api.public.i18n.GET_TRANSLATIONS, { params: httpParams })
      .catch((error: any) => {
        console.error('An error occured while fetching translations.', error);
        return Observable.of([]);
      });
  }

  /**
   * Checks if translations cache is still valid.
   * @param {string} module Translation category.
   * @param {string} hash Translation category hash.
   * @returns {boolean} true is translation cache is valid, otherwise false.
   */
  private isTranslationCacheValid(module: string, hash: string): boolean {
    const translationsHash = JSON.parse(window.localStorage.getItem(Storage.TRANSLATIONS_HASH));
    if ( translationsHash != null && translationsHash[module] === hash) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Add translations to local storage
   * @param {Object} translation Translation object.
   * @returns {void}
   */
  private addToLocalStorage(translation: Object): void {
    window.localStorage.setItem(this.getLocalStorageKey(translation['moduleName']),
      JSON.stringify(translation));
  }

  /**
   * Returns local storage key for translations for a category.
   * @param {string} key Translation category key.
   */
  private getLocalStorageKey(key: string): string {
    return 'translations_' + key + '_' + this.currentCulture;
  }

  /**
   * Get user Auth Token.
   * @returns {string} User auth token.
   */
  private getAuthToken(): string {
    return window.localStorage.getItem(Storage.AUTH_TOKEN);
  }

  /**
   * Parses user auth token.
   * @param {string} token User auth token.
   * @returns {Object} Payload JSON Object.
   */
  private parseToken(token: string): Object {
    if (token && token !== 'null') {
      const payload = token.split('.')[1].replace('-', '+').replace('_', '/');
      return JSON.parse(window.atob(payload));
    }
    return 'en-US';
  }
}



// WEBPACK FOOTER //
// ../UI/src/app/shared/translation/translation-loader.service.ts